// Helper types to ensure the build FromGroup out of DTO contains the DTO properties
// This is to find errors when the DTO changes and the FormGroup is not updated.

import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';

/** For strongly typed FormGroup out of a DTO class/interface.
 * Use it in FormGroup definition/FormBuilder.
 * Helper type to ensure the build FromGroup out of DTO contains the DTO properties
 * This is to find errors when the DTO changes and the FormGroup is not updated.
 * @example
 * const serviceRequestForm: FormGroup<PartialFormGroup<ServiceRequestDto>>
 * this.serviceRequestForm = this.formBuilder.group<
 *   PartialFormBuilder<ServiceRequestDto>
 * >({
 *   requestType: [currentItem?.requestType || null, Validators.required],
 * }) as FormGroup;
 */
export type PartialFormBuilder<T> = {
  [K in keyof T]?:
    | T[K]
    | AbstractControl<T[K]>
    | [T[K]]
    | [T[K], ValidatorFn | ValidatorFn[] | null];
};

/** For strongly typed FormGroup out of a DTO class/interface.
 * Use it in FormGroup declaration.
 * Helper type to ensure the build FromGroup out of DTO contains the DTO properties
 * This is to find errors when the DTO changes and the FormGroup is not updated.
 * @example
 * const serviceRequestForm: FormGroup<PartialFormGroup<ServiceRequestDto>>
 * this.serviceRequestForm = this.formBuilder.group<
 *   PartialFormBuilder<ServiceRequestDto>
 * >({
 *   requestType: [currentItem?.requestType || null, Validators.required],
 * }) as FormGroup;
 */
export type PartialFormGroup<T> = {
  [K in keyof T]?: T[K] extends Array<infer G>
    ? // If Array: Create a FormArray of FormGroups, where the Group shape is recursive
      FormArray<FormGroup<PartialFormGroup<G>>>
    : // If Not Array: Create a standard FormControl
      FormControl<T[K]>;
};

/** For strongly typed FormGroup out of a DTO class/interface.
 * Use it in FormGroup definition/FormBuilder.
 * Helper type to ensure the build FromGroup out of DTO contains the DTO properties
 * This is to find errors when the DTO changes and the FormGroup is not updated.
 * @example
 * const serviceRequestForm: FormGroup<RequiredFormGroup<ServiceRequestDto>>
 * this.serviceRequestForm = this.formBuilder.group<
 *   RequiredFormBuilder<ServiceRequestDto>
 * >({
 *   requestType: [currentItem?.requestType || null, Validators.required],
 * }) as FormGroup;
 */
export type RequiredFormBuilder<T> = {
  [K in keyof Required<T>]:
    | Required<T>[K]
    | AbstractControl<Required<T>[K]>
    | [Required<T>[K]]
    | [Required<T>[K], ValidatorFn | ValidatorFn[] | null];
};

/** For strongly typed FormGroup out of a DTO class/interface.
 * Use it in FormGroup declaration.
 * Helper type to ensure the build FromGroup out of DTO contains the DTO properties
 * This is to find errors when the DTO changes and the FormGroup is not updated.
 * @example
 * const serviceRequestForm: FormGroup<RequiredFormGroup<ServiceRequestDto>>
 * this.serviceRequestForm = this.formBuilder.group<
 *   RequiredFormBuilder<ServiceRequestDto>
 * >({
 *   requestType: [currentItem?.requestType || null, Validators.required],
 * }) as FormGroup;
 */
export type RequiredFormGroup<T> = {
  [K in keyof Required<T>]: Required<T>[K] extends Array<infer G>
    ? // If Array: Create a FormArray of FormGroups, where the Group shape is recursive
      FormArray<FormGroup<RequiredFormGroup<Required<G>>>>
    : // If Not Array: Create a standard FormControl
      FormControl<Required<T>[K]>;
};
